<script>
import Layout from '@layouts/auth'
import { authMethods } from '@state/helpers'
import getSecret from '@utils/get-secret'
import { required, minLength, sameAs, helpers } from 'vuelidate/lib/validators'

const passwordStrength = helpers.regex('passwordStrength', /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/)

export default {
  page: {
    title: 'Forget Password',
    meta: [{ name: 'description', content: `Forget Password to ${getSecret('TITLE')}` }],
  },
  components: { Layout },
  data() {
    return {
      email: '',
      error: null,
      tryingToReset: false,
      isResetError: false,
      isSuccess: false,
      successMessage: null,
      updatePwForm: {
        password: '',
        repeat_password: ''
      },
    }
  },
  validations: {
    updatePwForm: {
      password: {
        required,
        minLength: minLength(6),
        passwordStrength,
      },
      repeat_password: {
        required,
        sameAsPassword: sameAs('password')
      },
    },
  },
  computed: {
    getCompanyInfo: function () {
      return this.$store.state.appAuth.companyInfo || null
    },
  },
  methods: {
    ...authMethods,

    tryToReset() {
      if (this.$v.updatePwForm.$invalid) {
        this.$v.updatePwForm.$touch()
        return
      }
      this.tryingToReset = true
      // Reset the authError if it existed.
      this.error = null
      return this.resetPasswordAction({
        code: this.$route.params.uuid,
        password: this.updatePwForm.password
      })
        .then((data) => {
          this.tryingToReset = false
          this.isResetError = false
          this.isSuccess = true
          this.successMessage = data.detail
          this.$generate_toast(this, 'success', 'Redirecting to login page')
          setTimeout(()=>{
            this.$router.push({ name: 'login'})
          }, 3000)
        })
        .catch((error) => {
          this.tryingToReset = false
          this.isResetError = true
          this.isSuccess = false
          this.error = error.response.data.detail || "Internal Server Error"
        })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card bg-pattern">
          <div class="card-body p-4 pb-0">
            <div class="text-center m-auto">
              <a href="/"> 
                <span>
                  <b-img
                    v-if="getCompanyInfo"
                    :src="getCompanyInfo['dark_logo'] || require('@assets/images/logos/logo-dark.png')"
                    alt="login"
                    fluid
                  />
                </span>
              </a>
              <p class="text-muted mb-4 mt-3">
                {{ $t('login_section.enter_your_new_password') }}
              </p>
            </div>

            <b-alert v-if="error" v-model="isResetError" variant="danger" dismissible>
              {{error}}
            </b-alert>

            <b-alert v-id="successMessage" v-model="isSuccess" variant="success" dismissible>
              {{successMessage}}
            </b-alert>

            <b-form >
              <form-wrapper :validator="$v.updatePwForm ">
                <form-group name="password" :label="$t('password')">
                  <b-form-input
                    v-model="updatePwForm.password"
                    type="password"
                  /> 
                  <div class="text-warning mb-1">
                    <div v-if="!$v.updatePwForm.password.minLength">
                      *{{ $t('validation.minPasswordLength', { minLength: $v.updatePwForm.password.$params.minLength.min }) }}
                    </div>
                    <div v-if="!$v.updatePwForm.password.passwordStrength">
                      *{{ $t('validation.passwordStrength') }}
                    </div>
                  </div>
                </form-group>
                
                <form-group name="repeat_password" :label="$t('repeat_password')">
                  <b-form-input
                    v-model="updatePwForm.repeat_password"
                    type="password"
                  /> 
                  <div class="text-warning" v-if="!$v.updatePwForm.repeat_password.sameAsPassword">
                    *{{ $t('validation.sameAsPassword') }}
                  </div>   
                </form-group>       
              </form-wrapper>
            </b-form>
            
            <b-form-group id="button-group" class="mt-4">
              <b-button 
                type="submit" variant="primary" class="btn-block" 
                :disabled="tryingToReset || $v.updatePwForm.$invalid" @click="tryToReset"
              > 
                <span v-if="!tryingToReset">
                  {{ $t('login_section.change_password') }}
                </span>
                <b-spinner
                  v-if="tryingToReset"
                  small
                  label="Spinning"
                  variant="light"
                >
                </b-spinner>
              </b-button>
            </b-form-group>
          <!-- end card-body -->
          </div>
        <!-- end card -->
        </div>

        <div class="row mt-3">
          <div class="col-12 text-center">
            <p class="text-white-50">
              {{ $t('login_section.already_have_account') }}
              <router-link tag="a" to="/login" class="text-white ml-1">
                <b>{{ $t('login') }}</b>
              </router-link>
            </p>
          <!-- end col -->
          </div>
        <!-- end row -->
        </div>
      <!-- end col -->
      </div>
    <!-- end row -->
    </div>
  </Layout>
</template>

<style lang="scss" module></style>
